import type { CSSProperties } from 'react';
import type { BatchLead, LeadFieldsKey, LeadFieldsPatch } from './types';

/**
 * Pure helpers for the Batch grid's inline editing (spec 2026-09-17 §2,
 * amended 2026-09-30): the copy, the client-side validation, and how a patch
 * shows on a table row. No React, no I/O.
 */

/** Words the cells, the store and the grid's live region use. */
export const INLINE_EDIT_COPY = {
  /** "+ Add email" on a blank editable cell (the "+" is an icon). */
  addPrompt: (label: string) => `Add ${label}`,
  /** "+ Set" on a blank Temp cell. */
  setTemperature: 'Set',
  /** The Temp select's blank option. */
  temperatureNone: 'None',
  saving: 'Saving…',
  /** A 429 while it waits out `Retry-After` — never the server's raw text. */
  rateLimited: 'Too many saves at once. Retrying…',
  retrying: 'Couldn’t save yet. Retrying…',
  retry: 'Retry',
  discard: 'Discard',
  /** 404: spec §2.2. Missing, deleted and other-company leads all 404. */
  deleted: 'This lead was deleted',
  rateLimitedGaveUp: 'Too many saves at once. Wait a moment, then use the Retry button beside the cell.',
  networkGaveUp: 'Couldn’t reach the server. Check your connection, then use the Retry button beside the cell.',
  serverGaveUp: 'Couldn’t save: the server had a problem. Wait a moment, then use the Retry button beside the cell.',
  forbidden: 'You don’t have permission to edit this lead.',
  fallback: 'Couldn’t save.',
  notSaved: (fields: string, leadName: string, message: string) =>
    `Couldn’t save ${fields} for ${leadName}: ${message}`,
  invalidEmail: 'Enter a valid email, like name@company.com',
  tooLong: (max: number) => `Use ${max} characters or fewer`,
  /** Name popover (D2: First / Last, never a client-side split). */
  firstName: 'First name',
  lastName: 'Last name',
  save: 'Save',
  cancel: 'Cancel',
} as const;

/** Off screen but still read by assistive tech (failure text, the live region). */
export const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** Lower-case field names for prompts and accessible names. */
export const LEAD_FIELD_LABEL: Record<LeadFieldsKey, string> = {
  firstName: 'first name',
  lastName: 'last name',
  title: 'title',
  company: 'company',
  email: 'email',
  phone: 'phone',
  temperature: 'temperature',
};

/** Max length after trim, mirroring the backend's PersonEditSchema. */
const MAX_LENGTH: Record<Exclude<LeadFieldsKey, 'temperature'>, number> = {
  firstName: 200,
  lastName: 200,
  title: 200,
  company: 200,
  email: 320,
  phone: 64,
};

/** PersonEditSchema's email rule, verbatim. Blank is allowed. */
const EMAIL_RE = /^\S+@\S+\.\S+$/;

export const TEMPERATURE_VALUES = ['', 'hot', 'warm', 'cold'] as const;

/**
 * The client half of the server's validation, run on the TRIMMED value before
 * anything is queued: the same max lengths and the same email pattern as
 * `PersonEditSchema`, so the grid never sends a value it knows the server
 * would 400, and never blocks one the server accepts (no phone format rule:
 * the server has none). Returns the message to show, or null when valid.
 */
export function validateLeadField(field: LeadFieldsKey, value: string): string | null {
  if (field === 'temperature') {
    return (TEMPERATURE_VALUES as readonly string[]).includes(value) ? null : INLINE_EDIT_COPY.fallback;
  }
  const max = MAX_LENGTH[field];
  if (value.length > max) return INLINE_EDIT_COPY.tooLong(max);
  if (field === 'email' && value !== '' && !EMAIL_RE.test(value)) return INLINE_EDIT_COPY.invalidEmail;
  return null;
}

/**
 * The server's display-name rule (2026-09-30 D1/D3): trimmed "first last";
 * both empty keeps the stored name. Used only to show a name edit before the
 * server confirms it — the response's `preview` replaces it.
 */
export function deriveDisplayName(first: string, last: string, stored: string): string {
  const parts = [first.trim(), last.trim()].filter((p) => p !== '');
  return parts.length === 0 ? stored : parts.join(' ');
}

/**
 * Whether the backend behind these rows has inline editing's
 * `PATCH …/leads/:id/fields` route (viq-backend#117). The route can't be
 * probed without writing, but the same backend change adds `firstName` /
 * `lastName` to every list row, so any loaded row carrying either key (`''`
 * included: a stored blank) is a backend that has the route. Rows without
 * them come from an older backend, where every save would 404 and read as
 * "This lead was deleted": the grid then renders exactly as it does without
 * a `leadFieldsApi` (the Notes column's `latestNote` guard, for fields).
 */
export function rowsCarryLeadFields(rows: ReadonlyArray<Pick<BatchLead, 'firstName' | 'lastName'>>): boolean {
  return rows.some((r) => r.firstName !== undefined || r.lastName !== undefined);
}

/** A blank email/phone is `null` on a list row (the backend's blankToNull). */
function blankToNull(v: string): string | null {
  return v.trim() === '' ? null : v;
}

/**
 * How a patch reads on a table row: `company` is the row's `companyName`, a
 * blank email or phone is `null` (as list rows and the response carry them),
 * and a name part also re-derives `preview` from the row's other part. Only
 * the patched fields are touched — the rest of `row` (tags, assignment, …)
 * passes through untouched.
 */
export function applyPatchToRow(row: BatchLead, patch: LeadFieldsPatch): BatchLead {
  const next: BatchLead = { ...row };
  if (patch.company !== undefined) next.companyName = patch.company;
  if (patch.title !== undefined) next.title = patch.title;
  if (patch.email !== undefined) next.email = blankToNull(patch.email);
  if (patch.phone !== undefined) next.phone = blankToNull(patch.phone);
  if (patch.temperature !== undefined) next.temperature = patch.temperature;
  if (patch.firstName !== undefined || patch.lastName !== undefined) {
    const first = patch.firstName ?? row.firstName ?? '';
    const last = patch.lastName ?? row.lastName ?? '';
    if (patch.firstName !== undefined) next.firstName = patch.firstName;
    if (patch.lastName !== undefined) next.lastName = patch.lastName;
    next.preview = deriveDisplayName(first, last, row.preview);
  }
  return next;
}

/** The row keys a patch field shows up in. */
export const ROW_KEYS_OF_FIELD: Record<LeadFieldsKey, Array<keyof BatchLead>> = {
  firstName: ['firstName', 'preview'],
  lastName: ['lastName', 'preview'],
  title: ['title'],
  company: ['companyName'],
  email: ['email'],
  phone: ['phone'],
  temperature: ['temperature'],
};
